import { Add, Cancel } from '@mui/icons-material';
import {
  CircularProgress,
  Fade,
  IconButton,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getSuggestedResellsForConciliation } from '../../../../../../lib/api/reSell/factoring';
import FilterComponent from '../../../../../../lib/common/FilterNonSearch';
import CustomButton from '../../../../../../lib/designSystem/Button';
import EnhancedTable from '../../../../../../lib/designSystem/Table';
import {
  formatMoney,
  loggerDev,
  showAlert,
} from '../../../../../../lib/functions/utils';
import {
  ResellDocument,
  ResellSuggestedDocument,
} from '../../../../../../pages/Finance/Components/Funds/FactoringFundReSell/components/NewMultiResell/types';
import ResellAmountDetail from '../ResellAmountDetail';

interface DocumentsSuggestedResellsContainerProps {
  operationDate: Date;
  fundId: string;
  resells: ResellDocument[];
  disabled: boolean;
  amountToApply: number;
  setAmountToApply: (value: string) => void;
  selected: number[];
  setSelected: (value: number[]) => void;
  resellsToApply: ResellSuggestedDocument[];
  setResellsToApply: (value: ResellSuggestedDocument[]) => void;
  resellsSuggested: ResellSuggestedDocument[];
  setResellsSuggested: (value: ResellSuggestedDocument[]) => void;
}

const DocumentsSuggestedResellsContainer = (
  props: DocumentsSuggestedResellsContainerProps,
) => {
  const {
    operationDate,
    fundId,
    resells,
    disabled,
    amountToApply,
    setAmountToApply,
    selected,
    setSelected,
    resellsToApply,
    setResellsToApply,
    resellsSuggested,
    setResellsSuggested,
  } = props;
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [openPopper, setOpenPopper] = React.useState(false);
  const [filteredResellsSuggested, setFilteredResellsSuggested] = useState<
    ResellSuggestedDocument[]
  >([]);

  const maxAmount = resells.reduce(
    (acc: number, resell: ResellDocument) => acc + resell.amount,
    0,
  );

  const handleClickPopper = async () => {
    setOpenPopper(true);
    try {
      await setUp();
    } catch (error) {
      console.error('Error in setUp:', error);
    }
  };

  const handleClose = () => {
    setOpenPopper(false);
    setFilteredResellsSuggested([]);
  };

  const handleChangeAmountToConciliate = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const amount = event.target.value.split('.').join('').split('$').join('');
    if (isNaN(Number(amount))) return;
    if (Number(amount) > maxAmount) {
      return showAlert(
        dispatch,
        'error',
        'El monto a conciliar no puede ser mayor al monto a recibir',
      );
    }
    const amountToApply = Number(amount);
    const resell = resellsSuggested.find(
      (resell: ResellSuggestedDocument) =>
        resell.id === Number(event.target.name),
    );
    if (!resell) return;
    if (
      amountToApply < 0 ||
      Math.ceil(amountToApply) > Math.ceil(resell.netAmountRemaining)
    ) {
      return showAlert(
        dispatch,
        'error',
        'El monto a conciliar no puede ser mayor al saldo restante',
      );
    }
    resell.amountToConciliate = amountToApply;
    setResellsSuggested([...resellsSuggested]);
    handleSelectChange(resellsSuggested);
  };

  const cantSelectRow = (row: ResellSuggestedDocument) => {
    if (!maxAmount && maxAmount !== 0) return false;
    if (selected.includes(row.id)) return false;
    if (row.resellHasFutureConciliations) return true;
    return Number(amountToApply) + Number(row.amountToConciliate) > maxAmount;
  };

  const getTooltip = (id: number) => {
    const row = resellsSuggested.find(
      (resell: ResellSuggestedDocument) => resell.id === id,
    );
    if (!row) return null;
    return <ResellAmountDetail resell={row} />;
  };

  const documentsHeaders = [
    { key: 'id', label: 'Id venta' },
    { key: 'documentFolio', label: 'Folio' },
    { key: 'businessName', label: 'Cliente' },
    { key: 'stakeholderName', label: 'Deudor' },
    { key: 'fundName', label: 'Fondo' },
    {
      key: 'dueDate',
      label: 'Vencimiento',
      type: 'expiry-date-colored',
    },
    {
      key: 'netAmountRemaining',
      label: 'Saldo',
      type: 'money',
      customTooltip: getTooltip,
    },
    {
      key: 'amountToConciliate',
      label: 'Monto a conciliar',
      handleChange: handleChangeAmountToConciliate,
      type: 'money',
    },
  ];

  const prepareDocuments = (resellsSuggested: ResellSuggestedDocument[]) => {
    return resellsSuggested.map((resell: ResellSuggestedDocument) => {
      const resellToApply = resellsToApply.find(
        (res: ResellSuggestedDocument) => res.id === resell.id,
      );
      return {
        ...resell,
        amountToConciliate: resellToApply
          ? resellToApply.amountToConciliate
          : maxAmount
            ? Math.min(maxAmount, resell.netAmountRemaining)
            : resell.netAmountRemaining,
      };
    });
  };

  const setUp = async () => {
    setLoading(true);
    try {
      const queryParams = `operationDate=${operationDate.toISOString().split('T')[0]}&fundId=${fundId}`;
      const response = await getSuggestedResellsForConciliation(
        dispatch,
        queryParams,
      );
      const documentsPrepared = prepareDocuments(response);
      setResellsSuggested(documentsPrepared);
      setFilteredResellsSuggested(documentsPrepared);
    } catch (error) {
      loggerDev(error);
    }
    setLoading(false);
  };

  const handleSelectChange = (newResells?: ResellSuggestedDocument[]) => {
    if (!resellsSuggested.length || (newResells && !newResells.length)) return;
    const selectedDocuments = (newResells || resellsSuggested).filter(
      (resell: ResellSuggestedDocument) => selected.includes(resell.id),
    );
    const amountToApply = selectedDocuments.reduce(
      (acc: number, resell: ResellSuggestedDocument) =>
        acc + (resell.amountToConciliate || 0),
      0,
    );
    if (maxAmount && amountToApply > maxAmount) {
      return showAlert(
        dispatch,
        'error',
        'El monto a conciliar no puede ser mayor al monto a recibir',
      );
    }
    setAmountToApply(String(amountToApply.toFixed(0)));
    setResellsToApply(selectedDocuments);
  };

  useEffect(() => {
    handleSelectChange();
  }, [selected]);

  const handleSetFitleredData = (filteredData: ResellSuggestedDocument[]) => {
    setFilteredResellsSuggested(filteredData);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <TextField
          size={'small'}
          value={formatMoney(amountToApply)}
          fullWidth
          placeholder="0"
          label="Ventas a aplicar ($)"
          disabled
          sx={{
            '& .MuiInputBase-input': {
              textAlign: 'right',
            },
          }}
        />
        <IconButton onClick={handleClickPopper} disabled={disabled}>
          <Add />
          {selected.length !== 0 ? (
            <Typography
              style={{
                position: 'absolute',
                bottom: -5,
                right: 0,
                color: 'white',
                fontSize: 10,
                padding: '2px 6px',
                borderRadius: 50,
                fontWeight: '400',
                margin: '1px 2px',
                backgroundColor: '#297dff',
              }}
            >
              {selected.length}
            </Typography>
          ) : null}
        </IconButton>
      </div>
      <Popper
        style={{
          zIndex: 1300,
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '93%',
        }}
        open={openPopper}
        anchorEl={null}
        placement={'bottom'}
        transition={false}
        modifiers={[
          {
            name: 'preventOverflow',
            options: {
              altBoundary: true,
              tether: true,
              rootBoundary: 'document',
              padding: 8,
            },
          },
        ]}
      >
        <Paper
          style={{
            padding: 20,
            maxHeight: 600,
            overflow: 'scroll',
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center' }}>
              <Fade in={loading}>
                <CircularProgress />
              </Fade>
            </div>
          ) : (
            <div>
              {' '}
              <div
                style={{ display: 'flex', alignItems: 'center', width: '100%' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexGrow: 1,
                    padding: '10px 0px',
                  }}
                >
                  <Typography variant="h6">Ventas</Typography>
                  <FilterComponent
                    data={resellsSuggested}
                    filterFields={[
                      { key: 'documentFolio', filterName: 'Folio' },
                      { key: 'businessName', filterName: 'Cliente' },
                      { key: 'stakeholderName', filterName: 'Deudor' },
                    ]}
                    setFilteredData={handleSetFitleredData}
                  />
                </div>
                <IconButton
                  onClick={() => handleClose()}
                  style={{ marginLeft: 'auto' }}
                >
                  <Cancel />
                </IconButton>
              </div>
              <div>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'warning.main',
                    display: 'block',
                    marginBottom: 1,
                    fontStyle: 'italic',
                    fontSize: '0.75rem',
                  }}
                >
                  ⚠️ Los saldos y mora pendientes están calculados en base a la
                  fecha de operación. Ventas con conciliaciones futuras a la
                  fecha de operación no pueden ser aplicadas.
                </Typography>
              </div>
              <EnhancedTable
                dataHeaders={documentsHeaders}
                data={filteredResellsSuggested}
                selected={selected}
                setSelected={setSelected}
                cantSelectRow={cantSelectRow}
              />
              <div
                style={{
                  paddingTop: '24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body1">
                  Total a aplicar: {formatMoney(amountToApply)}
                </Typography>
                <CustomButton color="primary" onClick={handleClose}>
                  Seleccionar
                </CustomButton>
              </div>
            </div>
          )}
        </Paper>
      </Popper>
    </div>
  );
};

export default DocumentsSuggestedResellsContainer;
